/**
 * Mock email and calendar communication data for journey iteration.
 * Realistic data for Business School UK (cust_048) and contact Sarah Chen.
 */

export interface EmailInteraction {
  id: string;
  customer_id: string;
  direction: "inbound" | "outbound";
  from_address: string;
  from_name: string;
  to_addresses: string[];
  subject: string;
  snippet: string;
  sent_at: string;
  team_member_name: string;
  team_member_email: string;
  contact_id: string | null;
  contact_name: string | null;
  // Microsoft Graph read-state. Optional/nullable — omitted in older mock
  // fixtures and on the memory adapter path, which reads as "read".
  is_read?: boolean;
}

export interface CalendarInteraction {
  id: string;
  customer_id: string;
  title: string;
  start_at: string;
  end_at: string;
  location: string | null;
  attendees: { name: string; email: string }[];
  team_member_name: string;
  team_member_email: string;
  contact_id: string | null;
  contact_name: string | null;
  status: "confirmed" | "tentative" | "cancelled";
}

export type CommunicationItem =
  | { type: "email"; data: EmailInteraction }
  | { type: "meeting"; data: CalendarInteraction }
  | {
      type: "note";
      data: {
        id: string;
        note_text: string;
        created_at: string;
        user_id: string;
      };
    }
  | {
      type: "ticket";
      data: {
        id: string;
        title: string;
        status: string;
        created_at: string;
        resolved_at: string | null;
        due_date: string | null;
      };
    };

// --- Mock emails for Business School UK ---

export const mockEmails: EmailInteraction[] = [
  {
    id: "email-001",
    customer_id: "cust_048",
    direction: "inbound",
    from_address: "s.chen@businessschool.ac.uk",
    from_name: "Sarah Chen",
    to_addresses: ["ben.pollard@acmeprint.co.uk"],
    subject: "RE: Prospectus reprint timing",
    snippet:
      "Hi Ben, thanks for getting back to me so quickly. April works well for us — can we aim for w/c 14th April for delivery? We'll need 2,500 copies this time as we're covering two open days.",
    sent_at: "2026-04-07T14:22:00Z",
    team_member_name: "Ben Pollard",
    team_member_email: "ben.pollard@acmeprint.co.uk",
    contact_id: "con_048_1",
    contact_name: "Sarah Chen",
    is_read: false,
  },
  {
    id: "email-002",
    customer_id: "cust_048",
    direction: "outbound",
    from_address: "ben.pollard@acmeprint.co.uk",
    from_name: "Ben Pollard",
    to_addresses: ["s.chen@businessschool.ac.uk"],
    subject: "RE: Prospectus reprint timing",
    snippet:
      "Hi Sarah, absolutely — w/c 14th April is fine. I'll get a revised estimate over to you today for 2,500 copies. Same spec as last time or any changes to the cover stock?",
    sent_at: "2026-04-07T09:15:00Z",
    team_member_name: "Ben Pollard",
    team_member_email: "ben.pollard@acmeprint.co.uk",
    contact_id: "con_048_1",
    contact_name: "Sarah Chen",
  },
  {
    id: "email-003",
    customer_id: "cust_048",
    direction: "inbound",
    from_address: "s.chen@businessschool.ac.uk",
    from_name: "Sarah Chen",
    to_addresses: ["ben.pollard@acmeprint.co.uk"],
    subject: "Prospectus reprint timing",
    snippet:
      "Hi Ben, hope you're well. We need to reprint the undergraduate prospectus ahead of the summer open days. Could you let me know your availability and turnaround for a rerun of the last job?",
    sent_at: "2026-04-04T11:03:00Z",
    team_member_name: "Ben Pollard",
    team_member_email: "ben.pollard@acmeprint.co.uk",
    contact_id: "con_048_1",
    contact_name: "Sarah Chen",
  },
  {
    id: "email-004",
    customer_id: "cust_048",
    direction: "outbound",
    from_address: "ben.pollard@acmeprint.co.uk",
    from_name: "Ben Pollard",
    to_addresses: ["s.chen@businessschool.ac.uk"],
    subject: "Account review — Business School UK",
    snippet:
      "Hi Sarah, just following up on our call last week. As discussed, I've attached a summary of your print spend over the last 12 months and some suggestions for consolidating your regular jobs to get better pricing.",
    sent_at: "2026-03-28T16:45:00Z",
    team_member_name: "Ben Pollard",
    team_member_email: "ben.pollard@acmeprint.co.uk",
    contact_id: "con_048_1",
    contact_name: "Sarah Chen",
  },
  {
    id: "email-005",
    customer_id: "cust_048",
    direction: "inbound",
    from_address: "s.chen@businessschool.ac.uk",
    from_name: "Sarah Chen",
    to_addresses: ["ben.pollard@acmeprint.co.uk"],
    subject: "RE: Invoice query #INV-2026-0847",
    snippet:
      "Thanks Ben, that clears it up. The PO was raised under a different cost centre which is why finance flagged it. I've updated our records so it should go through now.",
    sent_at: "2026-03-21T10:12:00Z",
    team_member_name: "Ben Pollard",
    team_member_email: "ben.pollard@acmeprint.co.uk",
    contact_id: "con_048_1",
    contact_name: "Sarah Chen",
  },
  {
    id: "email-006",
    customer_id: "cust_048",
    direction: "outbound",
    from_address: "ben.pollard@acmeprint.co.uk",
    from_name: "Ben Pollard",
    to_addresses: ["s.chen@businessschool.ac.uk"],
    subject: "Invoice query #INV-2026-0847",
    snippet:
      "Hi Sarah, I noticed your finance team queried invoice #INV-2026-0847. Just to confirm — this relates to the February prospectus job (EST2026-001). Happy to provide any additional paperwork needed.",
    sent_at: "2026-03-20T14:30:00Z",
    team_member_name: "Ben Pollard",
    team_member_email: "ben.pollard@acmeprint.co.uk",
    contact_id: "con_048_1",
    contact_name: "Sarah Chen",
  },
  {
    id: "email-007",
    customer_id: "cust_048",
    direction: "inbound",
    from_address: "s.chen@businessschool.ac.uk",
    from_name: "Sarah Chen",
    to_addresses: ["lisa.martinez@acmeprint.co.uk"],
    subject: "Leaflet artwork approval",
    snippet:
      "Hi Lisa, artwork looks great — approved for print. One small tweak: can you make the QR code on the back panel slightly larger? Other than that, good to go.",
    sent_at: "2026-03-15T09:40:00Z",
    team_member_name: "Lisa Martinez",
    team_member_email: "lisa.martinez@acmeprint.co.uk",
    contact_id: "con_048_1",
    contact_name: "Sarah Chen",
  },
  {
    id: "email-008",
    customer_id: "cust_048",
    direction: "outbound",
    from_address: "lisa.martinez@acmeprint.co.uk",
    from_name: "Lisa Martinez",
    to_addresses: ["s.chen@businessschool.ac.uk"],
    subject: "Leaflet artwork for review",
    snippet:
      "Hi Sarah, please find attached the artwork proofs for the open day leaflets. Let me know if you'd like any changes before we go to print.",
    sent_at: "2026-03-14T15:20:00Z",
    team_member_name: "Lisa Martinez",
    team_member_email: "lisa.martinez@acmeprint.co.uk",
    contact_id: "con_048_1",
    contact_name: "Sarah Chen",
  },
];

// --- Mock emails for Meridian Retail Ltd (c-meridian-retail) ---
//
// Backs the "Attach an email" picker (CSR command-centre Slice 1, Phase D
// — see docs/features/csr-command-centre/) at /jobs/482913: the
// picker searches this customer's emails via `listCommunications({
// customerId, types: ["email"] })`, the same read CommunicationsPage uses.
// One entry (email-mrl-201) deliberately shares its from_address+subject
// with the already-confirmed jemail-482913-1 row in mock-data/job-comms.ts
// so the picker's "already attached to this job" marking has something to
// demonstrate; the rest are realistic unlinked candidates — a forwarded
// thread and a reply that never mention the job number, the exact gap this
// feature closes.
const hoursAgo = (hours: number): string =>
  new Date(Date.now() - hours * 3600_000).toISOString();

export const mockMeridianEmails: EmailInteraction[] = [
  {
    id: "email-mrl-201",
    customer_id: "c-meridian-retail",
    direction: "inbound",
    from_address: "sarah@meridianretail.co.uk",
    from_name: "Sarah Voss",
    to_addresses: ["ben.pollard@acmeprint.co.uk"],
    subject: "Re: Job 482913 — artwork approved",
    snippet: "…happy to approve the proof for 482913, please go ahead…",
    sent_at: hoursAgo(3),
    team_member_name: "Ben Pollard",
    team_member_email: "ben.pollard@acmeprint.co.uk",
    contact_id: "con_meridian_1",
    contact_name: "Sarah Voss",
    is_read: false,
  },
  {
    id: "email-mrl-202",
    customer_id: "c-meridian-retail",
    direction: "inbound",
    from_address: "tom@meridianretail.co.uk",
    from_name: "Tom Beckett",
    to_addresses: ["ben.pollard@acmeprint.co.uk"],
    subject: "FW: Proof ready for review",
    snippet:
      "Hi team, forwarding this along — Sarah signed off, just need your go-ahead before we send to print. Let me know if you need anything else from us before the weekend.",
    sent_at: hoursAgo(30),
    team_member_name: "Ben Pollard",
    team_member_email: "ben.pollard@acmeprint.co.uk",
    contact_id: "con_meridian_2",
    contact_name: "Tom Beckett",
  },
  {
    id: "email-mrl-203",
    customer_id: "c-meridian-retail",
    direction: "inbound",
    from_address: "sarah@meridianretail.co.uk",
    from_name: "Sarah Voss",
    to_addresses: ["ben.pollard@acmeprint.co.uk"],
    subject: "Quick one",
    snippet:
      "Hi Ben, following up from our call earlier — could we possibly bump the quantity slightly on the flyers? Will confirm the exact number tomorrow once I've checked with the stores team.",
    sent_at: hoursAgo(52),
    team_member_name: "Ben Pollard",
    team_member_email: "ben.pollard@acmeprint.co.uk",
    contact_id: "con_meridian_1",
    contact_name: "Sarah Voss",
  },
  {
    id: "email-mrl-204",
    customer_id: "c-meridian-retail",
    direction: "outbound",
    from_address: "ben.pollard@acmeprint.co.uk",
    from_name: "Ben Pollard",
    to_addresses: ["tom@meridianretail.co.uk"],
    subject: "Re: Delivery slot",
    snippet:
      "Sure, we can hold the delivery slot open — let us know once you've confirmed final numbers with the stores team and we'll lock it in.",
    sent_at: hoursAgo(70),
    team_member_name: "Ben Pollard",
    team_member_email: "ben.pollard@acmeprint.co.uk",
    contact_id: "con_meridian_2",
    contact_name: "Tom Beckett",
  },
];

mockEmails.push(...mockMeridianEmails);

// --- Mock calendar events for Meridian Retail Ltd (c-meridian-retail) ---
//
// Backs the "Attach a meeting" picker (CSR command-centre manual-link work
// — mirrors mockMeridianEmails above) at /jobs/482913: the picker
// searches this customer's meetings via `listCommunications({ customerId,
// types: ["meeting"] })`. One entry (cal-mrl-301) deliberately shares its
// title with the already-confirmed jmeeting-482913-1 row in
// mock-data/job-comms.ts so the picker's "already attached" marking has
// something to demonstrate; the rest are realistic unlinked candidates.
export const mockMeridianMeetings: CalendarInteraction[] = [
  {
    id: "cal-mrl-301",
    customer_id: "c-meridian-retail",
    title: "482913 flyers — artwork sign-off call",
    start_at: hoursAgo(30),
    end_at: hoursAgo(29.5),
    location: "Microsoft Teams",
    attendees: [
      { name: "Ben Pollard", email: "ben.pollard@acmeprint.co.uk" },
      { name: "Sarah Voss", email: "sarah@meridianretail.co.uk" },
    ],
    team_member_name: "Ben Pollard",
    team_member_email: "ben.pollard@acmeprint.co.uk",
    contact_id: "con_meridian_1",
    contact_name: "Sarah Voss",
    status: "confirmed",
  },
  {
    id: "cal-mrl-302",
    customer_id: "c-meridian-retail",
    title: "Meridian Retail — stores rollout planning",
    start_at: hoursAgo(-72), // 3 days from now
    end_at: hoursAgo(-71.5),
    location: "Phone call",
    attendees: [
      { name: "Ben Pollard", email: "ben.pollard@acmeprint.co.uk" },
      { name: "Tom Beckett", email: "tom@meridianretail.co.uk" },
    ],
    team_member_name: "Ben Pollard",
    team_member_email: "ben.pollard@acmeprint.co.uk",
    contact_id: "con_meridian_2",
    contact_name: "Tom Beckett",
    status: "confirmed",
  },
  {
    id: "cal-mrl-303",
    customer_id: "c-meridian-retail",
    title: "482913 — delivery slot walkthrough",
    start_at: hoursAgo(80),
    end_at: hoursAgo(79.75),
    location: "Microsoft Teams",
    attendees: [
      { name: "Ben Pollard", email: "ben.pollard@acmeprint.co.uk" },
      { name: "Sarah Voss", email: "sarah@meridianretail.co.uk" },
    ],
    team_member_name: "Ben Pollard",
    team_member_email: "ben.pollard@acmeprint.co.uk",
    contact_id: "con_meridian_1",
    contact_name: "Sarah Voss",
    status: "confirmed",
  },
];

// --- Mock calendar events for Business School UK ---

// Helper: dates relative to now so upcoming-interactions mock never goes stale.
function _daysFromNow(
  days: number,
  hour: number = 10,
  minute: number = 0,
): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  d.setHours(hour, minute, 0, 0);
  return d.toISOString();
}

export const mockCalendarEvents: CalendarInteraction[] = [
  {
    id: "cal-001",
    customer_id: "cust_048",
    title: "Account review — Business School UK",
    start_at: _daysFromNow(5, 10, 0),
    end_at: _daysFromNow(5, 10, 45),
    location: "Microsoft Teams",
    attendees: [
      { name: "Ben Pollard", email: "ben.pollard@acmeprint.co.uk" },
      { name: "Sarah Chen", email: "s.chen@businessschool.ac.uk" },
    ],
    team_member_name: "Ben Pollard",
    team_member_email: "ben.pollard@acmeprint.co.uk",
    contact_id: "con_048_1",
    contact_name: "Sarah Chen",
    status: "confirmed",
  },
  {
    id: "cal-002",
    customer_id: "cust_048",
    title: "Quarterly review call",
    start_at: _daysFromNow(-22, 14, 0),
    end_at: _daysFromNow(-22, 14, 30),
    location: "Microsoft Teams",
    attendees: [
      { name: "Ben Pollard", email: "ben.pollard@acmeprint.co.uk" },
      { name: "Sarah Chen", email: "s.chen@businessschool.ac.uk" },
    ],
    team_member_name: "Ben Pollard",
    team_member_email: "ben.pollard@acmeprint.co.uk",
    contact_id: "con_048_1",
    contact_name: "Sarah Chen",
    status: "confirmed",
  },
  {
    id: "cal-003",
    customer_id: "cust_048",
    title: "Site visit — print room walkthrough",
    start_at: _daysFromNow(-41, 11, 0),
    end_at: _daysFromNow(-41, 12, 30),
    location: "Business School UK, London Campus",
    attendees: [
      { name: "Ben Pollard", email: "ben.pollard@acmeprint.co.uk" },
      { name: "Lisa Martinez", email: "lisa.martinez@acmeprint.co.uk" },
      { name: "Sarah Chen", email: "s.chen@businessschool.ac.uk" },
    ],
    team_member_name: "Ben Pollard",
    team_member_email: "ben.pollard@acmeprint.co.uk",
    contact_id: "con_048_1",
    contact_name: "Sarah Chen",
    status: "confirmed",
  },
];

// --- Mock calendar events tied to CSR command-centre dashboard jobs ---
//
// The CSR command-centre mock (mock-data/csr-command-centre.ts) composes an
// "upcoming meetings" rail alongside the /home/csr job fixtures
// (csr-home.ts) — the next few meetings regardless of day, not just today's.
// The backend meeting model is title-only (no body/snippet), so these carry
// no job number directly — the command-centre builder correlates them to a
// dashboard job by `customer_id`, the same way a real composed view would
// join calendar rows to jobs via the customer. Tied to Meridian Group
// (c-meridian, job #730005, due today) and Kenton Care (c-kenton-care, job
// #730001, due today) so the rail visibly lines up with the feed/board.
// Spans several days (not just today) so the rail visibly reads as
// "upcoming" rather than "today".
function _todayAt(hour: number, minute: number = 0): string {
  const d = new Date();
  d.setHours(hour, minute, 0, 0);
  return d.toISOString();
}

function _daysFromNowAt(
  days: number,
  hour: number,
  minute: number = 0,
): string {
  const d = new Date(Date.now() + days * 86_400_000);
  d.setHours(hour, minute, 0, 0);
  return d.toISOString();
}

export const csrCommandCentreCalendarEvents: CalendarInteraction[] = [
  {
    id: "cal-csr-001",
    customer_id: "c-meridian",
    title: "Delegate pack sign-off — Meridian Group",
    start_at: _todayAt(10, 30),
    end_at: _todayAt(11, 0),
    location: "Microsoft Teams",
    attendees: [
      { name: "Ben Pollard", email: "ben.pollard@acmeprint.co.uk" },
      { name: "Priya Anand", email: "priya.anand@meridiangroup.co.uk" },
    ],
    team_member_name: "Ben Pollard",
    team_member_email: "ben.pollard@acmeprint.co.uk",
    contact_id: null,
    contact_name: "Priya Anand",
    status: "confirmed",
  },
  {
    id: "cal-csr-002",
    customer_id: "c-kenton-care",
    title: "Leaflet quantities check-in — Kenton Care",
    start_at: _todayAt(13, 0),
    end_at: _todayAt(13, 20),
    location: "Phone call",
    attendees: [
      { name: "Ben Pollard", email: "ben.pollard@acmeprint.co.uk" },
      { name: "Owen Marsh", email: "owen.marsh@kentoncare.org.uk" },
    ],
    team_member_name: "Ben Pollard",
    team_member_email: "ben.pollard@acmeprint.co.uk",
    contact_id: null,
    contact_name: "Owen Marsh",
    status: "confirmed",
  },
  {
    id: "cal-csr-003",
    customer_id: "c-acme",
    title: "Quarterly account review — Acme Ltd",
    start_at: _todayAt(15, 30),
    end_at: _todayAt(16, 15),
    location: "Microsoft Teams",
    attendees: [
      { name: "Ben Pollard", email: "ben.pollard@acmeprint.co.uk" },
      { name: "Chris Doyle", email: "chris.doyle@acmeltd.co.uk" },
      { name: "Lisa Martinez", email: "lisa.martinez@acmeprint.co.uk" },
    ],
    team_member_name: "Ben Pollard",
    team_member_email: "ben.pollard@acmeprint.co.uk",
    contact_id: null,
    contact_name: "Chris Doyle",
    status: "confirmed",
  },
  {
    id: "cal-csr-004",
    customer_id: "c-brightside",
    title: "Ward boards proof walkthrough — Brightside Health",
    start_at: _daysFromNowAt(2, 11, 0),
    end_at: _daysFromNowAt(2, 11, 30),
    location: "Phone call",
    attendees: [
      { name: "Ben Pollard", email: "ben.pollard@acmeprint.co.uk" },
      { name: "Dana Fields", email: "dana.fields@brightsidehealth.org.uk" },
    ],
    team_member_name: "Ben Pollard",
    team_member_email: "ben.pollard@acmeprint.co.uk",
    contact_id: null,
    contact_name: "Dana Fields",
    status: "confirmed",
  },
  {
    id: "cal-csr-005",
    customer_id: "c-nhs-trust",
    title: "Vaccination leaflets sign-off — NHS Trust",
    start_at: _daysFromNowAt(5, 9, 30),
    end_at: _daysFromNowAt(5, 10, 0),
    location: "Microsoft Teams",
    attendees: [
      { name: "Ben Pollard", email: "ben.pollard@acmeprint.co.uk" },
      { name: "Grace Oduya", email: "grace.oduya@nhstrust.nhs.uk" },
    ],
    team_member_name: "Ben Pollard",
    team_member_email: "ben.pollard@acmeprint.co.uk",
    contact_id: null,
    contact_name: "Grace Oduya",
    status: "confirmed",
  },
];

mockCalendarEvents.push(...csrCommandCentreCalendarEvents);
mockCalendarEvents.push(...mockMeridianMeetings);

// --- Helper to build a unified, sorted communication timeline ---

export function buildCommunicationTimeline(
  emails: EmailInteraction[],
  calendarEvents: CalendarInteraction[],
  notes: {
    id: string;
    note_text: string;
    created_at: string;
    user_id: string;
  }[],
  tickets: {
    id: string;
    title: string;
    status: string;
    created_at: string;
    resolved_at: string | null;
    due_date: string | null;
  }[],
): CommunicationItem[] {
  const items: (CommunicationItem & { _sort: string })[] = [
    ...emails.map((e) => ({
      type: "email" as const,
      data: e,
      _sort: e.sent_at,
    })),
    ...calendarEvents.map((c) => ({
      type: "meeting" as const,
      data: c,
      _sort: c.start_at,
    })),
    ...notes.map((n) => ({
      type: "note" as const,
      data: n,
      _sort: n.created_at,
    })),
    ...tickets.map((t) => ({
      type: "ticket" as const,
      data: t,
      _sort: t.created_at,
    })),
  ];

  items.sort(
    (a, b) => new Date(b._sort).getTime() - new Date(a._sort).getTime(),
  );

  return items.map(({ type, data }) => ({ type, data }) as CommunicationItem);
}
